import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Header } from "@/components/Header";
import { ProfileSidebar } from "@/components/ProfileSidebar";
import {
  HeroBanner,
  PetsSection,
  ReservasSection,
  MensagensSection,
  AvaliacoesSectionTutor,
} from "@/components/DashboardSections";
import type { Pet, Reserva, Mensagem, DashboardAvaliacoes } from "@/types/dashboard";

import { normalizeTutorProfileItems } from "@/lib/profileCompletion";
type ProfileItem = {
  label: string;
  filled: boolean;
};

type UserData = {
  name: string;
  bio: string;
  avatarUrl: string | null;
  profileItems: ProfileItem[];
  profilePercent: number;
};

type DashboardResponse = {
  user: UserData;
  pets: Pet[];
  reservas: Reserva[];
  mensagens: Mensagem[];
  avaliacoes?: DashboardAvaliacoes;
};

const TutorDashboard = () => {
  const location = useLocation();
  const view = new URLSearchParams(location.search).get("view");
  const reservasView = view === "reservas";
  const avaliacoesView = view === "avaliacoes";
  const [data, setData] = useState<DashboardResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);

    const url = reservasView
      ? "/tutor/dashboard/data?reservas=all"
      : "/tutor/dashboard/data";

    fetch(url, {
      headers: {
        Accept: "application/json",
      },
      credentials: "include",
    })
      .then(async (res) => {
        if (!res.ok) {
          throw new Error("Erro ao carregar dashboard");
        }
        return res.json();
      })
      .then((json) => {
        setData({
          ...json,
          user: {
            ...json.user,
            profileItems: normalizeTutorProfileItems(json.user?.profileItems),
          },
          pets: (json.pets || []).map((pet: any) => ({
            ...pet,
            id: String(pet.id),
            especie: pet.especie || "Pet",
          })),
          reservas: (json.reservas || []).map((reserva: any) => ({
            ...reserva,
            id: String(reserva.id),
            status: ["confirmada", "pendente", "recusada", "cancelada", "concluida", "finalizada"].includes(reserva.status)
              ? reserva.status
              : "pendente",
          })),
          mensagens: (json.mensagens || []).map((mensagem: any) => ({
            ...mensagem,
            id: String(mensagem.id),
          })),
        });
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [reservasView, avaliacoesView]);

  if (loading) {
    return <div className="p-6">Carregando dashboard...</div>;
  }

  if (!data) {
    return <div className="p-6">Não foi possível carregar o dashboard.</div>;
  }

  const firstName = data.user.name.split(" ")[0];
  const dashboardHome = !reservasView && !avaliacoesView;

  return (
    <>
      <Header activeTab="tutores" isCompact hideAudienceNav />
      <div className="min-h-screen gradient-hero">
        <div className="container max-w-6xl mx-auto px-4 py-6 md:py-10">
          <div className="flex flex-col lg:flex-row gap-6">
            <aside className="w-full lg:w-72 shrink-0">
              <ProfileSidebar
                userName={firstName}
                bio={data.user.bio || ""}
                avatarUrl={data.user.avatarUrl || ""}
                profileItems={data.user.profileItems || []}
              />
            </aside>

            <main className="flex-1 space-y-5">
              {dashboardHome && <HeroBanner firstName={firstName} />}
              {dashboardHome && <PetsSection pets={data.pets} />}
              {reservasView && (
                <div className="rounded-2xl bg-white/80 p-5 shadow-sm">
                  <h1 className="text-2xl font-bold text-foreground">Minhas Reservas</h1>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Acompanhe todas as reservas do seu painel.
                  </p>
                </div>
              )}
              {avaliacoesView && (
                <div className="rounded-2xl bg-white/80 p-5 shadow-sm">
                  <h1 className="text-2xl font-bold text-foreground">Avaliações</h1>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Veja sua reputação como tutor e o histórico das avaliações enviadas.
                  </p>
                </div>
              )}
              {avaliacoesView ? (
                <AvaliacoesSectionTutor avaliacoes={data.avaliacoes} />
              ) : (
                <ReservasSection reservas={data.reservas} />
              )}
              {dashboardHome && <MensagensSection mensagens={data.mensagens} />}
            </main>
          </div>
        </div>
      </div>
    </>
  );
};

export default TutorDashboard;
